import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import api from '../../utils/api';

export default function Step5ServiceDeclarations({ formData, errors, updateField, clearError, setError }) {
  const [centreSearch, setCentreSearch] = useState('');
  const [testCentres, setTestCentres] = useState([]);
  const [referralChecking, setReferralChecking] = useState(false);

  // Fetch test centres from DB — deduplicate names in case DB has duplicates
  useEffect(() => {
    api.get('/test-centres')
      .then(data => {
        const names = (data.testCentres || []).map(tc => tc.name);
        setTestCentres([...new Set(names)]);
      })
      .catch(() => setTestCentres([]));
  }, []);
  const canvasRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [lastPos, setLastPos] = useState({ x: 0, y: 0 });
  const [hasSigned, setHasSigned] = useState(false);

  const h = (e) => { updateField(e.target.name, e.target.value); clearError(e.target.name); };
  const handleReferrerPRN = (e) => {
    const val = e.target.value.replace(/[^0-9]/g, '').slice(0, 6);
    updateField('referring_instructor_adi', val);
    clearError('referring_instructor_adi');
  };

  // ── Canvas signature ────────────────────────────────────────
  const getPos = (e, canvas) => {
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    const src = e.touches ? e.touches[0] : e;
    return {
      x: (src.clientX - rect.left) * scaleX,
      y: (src.clientY - rect.top) * scaleY,
    };
  };

  const startDraw = useCallback((e) => {
    e.preventDefault();
    const pos = getPos(e, canvasRef.current);
    setIsDrawing(true);
    setLastPos(pos);
  }, []);

  const draw = useCallback((e) => {
    e.preventDefault();
    if (!isDrawing) return;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const pos = getPos(e, canvas);
    ctx.beginPath();
    ctx.moveTo(lastPos.x, lastPos.y);
    ctx.lineTo(pos.x, pos.y);
    ctx.strokeStyle = '#0A1F4E';
    ctx.lineWidth = 2.5;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.stroke();
    setLastPos(pos);
  }, [isDrawing, lastPos]);

  const stopDraw = useCallback(() => {
    if (!isDrawing) return;
    setIsDrawing(false);
    setHasSigned(true);
    updateField('signature_data', canvasRef.current.toDataURL());
    clearError('signature_data');
  }, [isDrawing, updateField, clearError]);

  const clearSignature = () => {
    const canvas = canvasRef.current;
    canvas.getContext('2d').clearRect(0, 0, canvas.width, canvas.height);
    setHasSigned(false);
    updateField('signature_data', '');
  };

  // Attach non-passive touch listeners so preventDefault works
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    canvas.addEventListener('touchstart', startDraw, { passive: false });
    canvas.addEventListener('touchmove', draw, { passive: false });
    canvas.addEventListener('touchend', stopDraw);
    return () => {
      canvas.removeEventListener('touchstart', startDraw);
      canvas.removeEventListener('touchmove', draw);
      canvas.removeEventListener('touchend', stopDraw);
    };
  }, [startDraw, draw, stopDraw]);

  const handleReferralCode = (e) => {
    // Uppercase, strip non-alphanum/dash — accept FT-XXXXXX, ADI-XXXXXX or plain 6-char legacy codes
    const val = e.target.value.toUpperCase().replace(/[^A-Z0-9-]/g, '').slice(0, 10);
    updateField('referral_code_used', val);
    clearError('referral_code_used');
  };

  const handleReferralCodeBlur = async () => {
    const code = (formData.referral_code_used || '').trim().toUpperCase();
    if (!code) return;
    if (!/^((FT|ADI)-[A-Z0-9]{4,8}|[A-Z0-9]{6})$/.test(code)) return; // format error shown by validate()
    setReferralChecking(true);
    try {
      const prn = (formData.referring_instructor_adi || '').trim();
      const params = { code };
      if (prn && /^\d{6}$/.test(prn)) params.prn = prn;
      const res = await api.get('/applications/validate-referral', { params });
      if (res.valid === false) {
        setError('referral_code_used', res.message || 'Referral code not found.');
      } else {
        clearError('referral_code_used');
        if (res.instructorName && !formData.referring_instructor_name?.trim()) {
          updateField('referring_instructor_name', res.instructorName);
        }
      }
    } catch (_) {
      // Non-blocking
    } finally {
      setReferralChecking(false);
    }
  };
  const handleSortCode = (e) => {
    let val = e.target.value.replace(/[^0-9]/g, '');
    if (val.length > 6) val = val.slice(0, 6);
    if (val.length > 4) val = val.slice(0, 2) + '-' + val.slice(2, 4) + '-' + val.slice(4);
    else if (val.length > 2) val = val.slice(0, 2) + '-' + val.slice(2);
    updateField('sort_code', val);
    clearError('sort_code');
  };

  const handleAccountNumber = (e) => {
    const val = e.target.value.replace(/[^0-9]/g, '').slice(0, 8);
    updateField('account_number', val);
    clearError('account_number');
  };

  // Robust word-based search: every word in the query must appear in the centre name.
  // This prevents a partial character like "b" matching everything, and ensures
  // typing "sidcup" only matches centres that actually contain the word "sidcup".
  const filteredCentres = useMemo(() => {
    const q = centreSearch.trim().toLowerCase();
    if (!q) return testCentres;
    const words = q.split(/\s+/).filter(w => w.length > 0);
    return testCentres.filter(tc => {
      const name = tc.toLowerCase();
      return words.every(word => name.includes(word));
    });
  }, [testCentres, centreSearch]);

  const toggleCentre = (centre) => {
    const current = formData.test_centres || [];
    const updated = current.includes(centre)
      ? current.filter(c => c !== centre)
      : [...current, centre];
    updateField('test_centres', updated);
    clearError('test_centres');
  };

  const toggleDecl = (name) => {
    updateField(name, !formData[name]);
    clearError(name);
  };
  
  return (
    <div className="adi-step-content">
      <div className="adi-step-header">
        <span className="adi-step-num-badge">5</span>
        <div>
          <h2 className="adi-step-title">Service Details &amp; Declaration</h2>
          <p className="adi-step-desc">Your teaching areas, availability and final sign-off</p>
        </div>
      </div>

      {/* ── Test Centres Slider ── */}
      <div className="adi-subsection">
        <h3 className="adi-subsection-title">Test Centres You Cover *</h3>
        <p className="adi-upload-hint">Select every test centre area where you teach learners</p>

        {/* Search + count */}
        <div className="adi-centre-controls">
          <input
            type="text"
            className="adi-centre-search"
            placeholder="🔍 Search test centres…"
            value={centreSearch}
            onChange={e => setCentreSearch(e.target.value)}
          />
          {(formData.test_centres || []).length > 0 && (
            <span className="adi-centre-count-badge">
              {(formData.test_centres || []).length} selected
            </span>
          )}
        </div>

        {/* Selected tags */}
        {(formData.test_centres || []).length > 0 && (
          <div className="adi-selected-tags">
            {(formData.test_centres || []).map(tc => (
              <span key={tc} className="adi-selected-tag" onClick={() => toggleCentre(tc)}>
                {tc} <span className="adi-tag-remove">✕</span>
              </span>
            ))}
          </div>
        )}

        {/* Scrollable checkbox list — replaces paginated grid for better mobile UX */}
        {(() => {
          return (
            <div className={`adi-centres-scroll-list${errors.test_centres ? ' adi-centres-scroll-list--err' : ''}`}>
              {filteredCentres.length > 0 ? filteredCentres.map(tc => {
                const isSelected = (formData.test_centres || []).includes(tc);
                return (
                  <label
                    key={tc}
                    className={`adi-centre-list-item${isSelected ? ' adi-centre-list-item--selected' : ''}`}
                  >
                    <input
                      type="checkbox"
                      className="adi-centre-check"
                      checked={isSelected}
                      onChange={() => toggleCentre(tc)}
                    />
                    <span>{tc}</span>
                  </label>
                );
              }) : (
                <div className="adi-centre-empty">No centres match "{centreSearch}"</div>
              )}
            </div>
          );
        })()}
        {errors.test_centres && <span className="adi-err">{errors.test_centres}</span>}
      </div>

      {/* ── Bank Details ── */}
      <div className="adi-subsection">
        <h3 className="adi-subsection-title">Bank Details <span className="adi-subsection-note">(For Referral &amp; Bonus Payments)</span></h3>
        <p className="adi-upload-hint">Used for referral bonuses. Stored securely and never shared.</p>
        <div className="adi-field-grid">
          <div className="adi-field adi-field--full">
            <label>Account Holder Name *</label>
            <input name="account_holder_name" value={formData.account_holder_name || ''} onChange={h}
              placeholder="Full name as on bank account"
              className={errors.account_holder_name ? 'err' : ''} />
            {errors.account_holder_name && <span className="adi-err">{errors.account_holder_name}</span>}
          </div>
          <div className="adi-field">
            <label>Bank Name *</label>
            <input name="bank_name" value={formData.bank_name || ''} onChange={h}
              placeholder="e.g. Barclays, HSBC, Lloyds"
              className={errors.bank_name ? 'err' : ''} />
            {errors.bank_name && <span className="adi-err">{errors.bank_name}</span>}
          </div>
          <div className="adi-field">
            <label>Sort Code *</label>
            <input name="sort_code" value={formData.sort_code || ''} onChange={handleSortCode}
              placeholder="12-34-56" maxLength={8} inputMode="numeric"
              className={errors.sort_code ? 'err' : ''} />
            {errors.sort_code
              ? <span className="adi-err">{errors.sort_code}</span>
              : <p className="adi-field-note">6 digits — auto-formatted as XX-XX-XX</p>
            }
          </div>
          <div className="adi-field">
            <label>Account Number *</label>
            <input name="account_number" value={formData.account_number || ''} onChange={handleAccountNumber}
              placeholder="12345678" maxLength={8} inputMode="numeric"
              className={errors.account_number ? 'err' : ''} />
            {errors.account_number
              ? <span className="adi-err">{errors.account_number}</span>
              : <p className="adi-field-note">Exactly 8 digits</p>
            }
          </div>
        </div>
        <label className={`adi-declaration-item adi-declaration-item--bank${errors.declaration_bank ? ' adi-declaration-item--err' : ''}`}>
          <input type="checkbox" checked={!!formData.declaration_bank} onChange={() => toggleDecl('declaration_bank')} />
          <span>I confirm these bank details are correct and belong to me. *</span>
        </label>
        {errors.declaration_bank && <span className="adi-err">{errors.declaration_bank}</span>}
      </div>

      {/* ── Instructor Referral ── */}
      <div className="adi-subsection">
        <h3 className="adi-subsection-title">Instructor Referral <span className="adi-subsection-note">(Optional)</span></h3>
        <p className="adi-upload-hint">Were you referred by another instructor?</p>
        <div className="adi-radio-group" style={{ marginBottom: '1rem' }}>
          {[{ value: 'yes', label: 'Yes' }, { value: 'no', label: 'No' }].map(opt => (
            <label key={opt.value} className="adi-radio-option">
              <input type="radio" name="referred_by_instructor" value={opt.value}
                checked={formData.referred_by_instructor === opt.value} onChange={h} />
              {opt.label}
            </label>
          ))}
        </div>
        {formData.referred_by_instructor === 'yes' && (
          <div className="adi-field-grid adi-referral-fields">
            <div className="adi-field">
              <label>Referring Instructor Name</label>
              <input name="referring_instructor_name" value={formData.referring_instructor_name || ''}
                onChange={h} placeholder="Full name of instructor who referred you" />
            </div>
            <div className="adi-field">
              <label>Referring Instructor ADI / PRN</label>
              <input name="referring_instructor_adi" value={formData.referring_instructor_adi || ''}
                onChange={handleReferrerPRN} placeholder="Their 6-digit PRN"
                inputMode="numeric" maxLength={6}
                className={errors.referring_instructor_adi ? 'err' : ''} />
              {errors.referring_instructor_adi && <span className="adi-err">{errors.referring_instructor_adi}</span>}
            </div>
            <div className="adi-field adi-field--full">
              <label>Referral Code <span className="rfq-req">*</span></label>
              <div style={{ position: 'relative' }}>
                <input name="referral_code_used" value={formData.referral_code_used || ''}
                  onChange={handleReferralCode}
                  onBlur={handleReferralCodeBlur}
                  placeholder="e.g. FT-ABC123 or ADI-5S087B"
                  maxLength={10}
                  inputMode="text"
                  className={errors.referral_code_used ? 'err' : ''} />
                {referralChecking && (
                  <span style={{ position: 'absolute', right: '0.75rem', top: '50%', transform: 'translateY(-50%)', fontSize: '0.8rem', color: '#64748b' }}>Checking…</span>
                )}
              </div>
              {errors.referral_code_used
                ? <span className="adi-err">{errors.referral_code_used}</span>
                : <p className="adi-field-note">Format: FT-XXXXXX code — verified against our records</p>
              }
            </div>
          </div>
        )}
      </div>

      {/* ── Declarations ── */}
      <div className="adi-subsection">
        <h3 className="adi-subsection-title">Declarations *</h3>
        <p className="adi-upload-hint">Read and confirm each declaration before submitting</p>
        <div className="adi-declarations">
          {[
            { key: 'declaration_adi', text: 'I confirm I am a DVSA-registered ADI and all information provided is accurate and correct.' },
            { key: 'declaration_vehicle', text: 'I confirm my vehicle is fully insured and compliant with UK driving instruction regulations.' },
            { key: 'declaration_terms', text: 'I agree to the Fast Track Driving School Terms & Conditions and Referral Agreement.' },
          ].map(d => (
            <label key={d.key} className={`adi-declaration-item${errors[d.key] ? ' adi-declaration-item--err' : ''}`}>
              <input type="checkbox" checked={!!formData[d.key]} onChange={() => toggleDecl(d.key)} />
              <span>{d.text}</span>
            </label>
          ))}
          {(errors.declaration_adi || errors.declaration_vehicle || errors.declaration_terms) && (
            <span className="adi-err">Please confirm all declarations to proceed</span>
          )}
        </div>
      </div>

      {/* ── Agreement Document ── */}
      <div className="adi-subsection">
        <h3 className="adi-subsection-title">Referral Agreement — Please Read *</h3>
        <p className="adi-upload-hint">Please read the full agreement before signing. Your signature below confirms your acceptance of all terms.</p>
        <div className="adi-agreement-doc">
          <div className="adi-agreement-header">
            FAST TRACK DRIVING SCHOOL UK<br />APPROVED DRIVING INSTRUCTOR (ADI) REFERRAL AGREEMENT
          </div>
          <p>This Agreement is made between: <strong>FAST TRACK DRIVING SCHOOL UK</strong> ("the Company") and the Approved Driving Instructor ("the Instructor").</p>
          <h4>1. Purpose of Agreement</h4>
          <p>The purpose of this Agreement is to define the referral-based working relationship between the Company and the Instructor. The Company operates as a student referral and marketing platform, connecting learner drivers with independent Approved Driving Instructors.</p>
          <h4>2. Independent Contractor Status</h4>
          <p>The Instructor is engaged strictly as an independent contractor. Nothing in this Agreement shall be construed as creating an employer–employee relationship, partnership, agency, or joint venture. The Instructor is solely responsible for:</p>
          <ul>
            <li>Income tax, National Insurance, and VAT (if applicable)</li>
            <li>Maintaining valid ADI registration with the DVSA</li>
            <li>Vehicle ownership, maintenance, fuel, and insurance</li>
            <li>Compliance with all applicable UK laws and regulations</li>
          </ul>
          <h4>3. Student Referral &amp; Payment Model</h4>
          <p>3.1 The Company operates as a student referral and marketing platform connecting Students with independent Approved Driving Instructors.</p>
          <p>3.2 The Company collects an upfront booking fee from the Student. This upfront fee varies depending on the lesson package selected by the Student (Trial, Bronze, Silver, or Gold).</p>
          <p>3.3 This upfront booking fee represents a referral and marketing fee payable to Fast Track Driving School for providing verified students to the Instructor.</p>
          <p>3.4 <strong>The Instructor must not request or accept any payment from the Student that corresponds to the upfront fee already paid to the Company.</strong></p>
          <p>3.5 After the first lesson is completed, the Instructor may continue providing lessons and collect the remaining lesson payments directly from the Student.</p>
          <p>3.6 The standard lesson rate used by the Company is £37 per hour.</p>
          <p>3.7 The Company has no obligation to provide a minimum number of Students or referrals.</p>
          <h4>4. Session Commitment</h4>
          <p>Once a Student is approved and scheduled for a lesson, the Instructor may not cancel before the first session. Any attempt to cancel after approval to bypass the referral system will be considered a breach of contract and may result in penalties and termination of referral privileges.</p>
          <h4>5. Referral &amp; Marketing Fee</h4>
          <p>5.1 The upfront booking fee paid by the Student represents a non-refundable referral and marketing fee payable to the Company.</p>
          <p>5.2 The amount of the referral and marketing fee varies depending on the lesson package selected by the Student.</p>
          <p>5.3 The Company retains 100% of the upfront booking fee.</p>
          <p>5.4 The Instructor shall collect the remaining lesson payments directly from the Student after completion of the first lesson.</p>
          <p>5.5 All fees are exclusive of VAT unless otherwise stated.</p>
          <h4>6. Non-Circumvention &amp; Fair Use</h4>
          <p>6.1 The Instructor agrees not to bypass or attempt to bypass Fast Track Driving School's referral and upfront referral/marketing fee system by accepting Students privately, encouraging direct bookings, or avoiding the Company's platform.</p>
          <p>6.2 The Company collects an upfront referral/marketing fee from the Student at the time of booking, depending on the selected lesson package. The Instructor may only provide lessons after the Company has completed the referral process.</p>
          <p>6.3 Any breach of this clause will result in immediate removal from the Fast Track Driving School platform, and the Instructor will not be eligible to rejoin in the future.</p>
          <h4>Important Obligations</h4>
          <p>The Instructor agrees to:</p>
          <ul>
            <li>Not cancel the first lesson after approving a Student booking</li>
            <li>Maintain valid ADI licence, DBS, insurance, and MOT</li>
            <li>Notify the Company immediately when any document expires</li>
            <li>Deliver instruction professionally and safely</li>
            <li>Not bypass or circumvent the Company's referral system</li>
            <li>Comply with UK laws and DVSA regulations</li>
          </ul>
          <h4>7. Professional Standards &amp; Document Updates</h4>
          <p>The Instructor agrees to deliver instruction professionally, maintain a DVSA-compliant vehicle, treat Students respectfully, keep all documents up to date, and notify Fast Track Driving School immediately when any documents expire or conduct that may damage the Company's reputation occurs.</p>
          <h4>8. Branding &amp; Representation</h4>
          <p>The Instructor shall not misuse the Company's name, branding, or reputation, and must not represent themselves as an employee, partner, or agent of the Company.</p>
          <h4>9. Data Protection (UK GDPR)</h4>
          <p>Both parties agree to comply with UK GDPR and the Data Protection Act 2018. Student information shall only be used for lesson-related communication and must not be shared, sold, or misused.</p>
          <h4>10. Liability &amp; Indemnity</h4>
          <p>The Company accepts no liability for driving instruction, accidents, or claims arising from lessons. The Instructor agrees to fully indemnify the Company against all claims, losses, damages, or expenses arising from their services, vehicle, or conduct.</p>
          <h4>11. Termination</h4>
          <p>Either party may terminate this Agreement with 7 days' written notice. Any outstanding fees or obligations remain payable after termination.</p>
          <h4>12. Entire Agreement &amp; Governing Law</h4>
          <p>This Agreement constitutes the entire understanding between the parties. Any amendments must be in writing and signed by both parties. This Agreement is governed by the laws of England and Wales.</p>
        </div>
      </div>

      {/* ── Signature pad ── */}
      <div className="adi-subsection">
        <h3 className="adi-subsection-title">Signature *</h3>
        <p className="adi-upload-hint">Draw your signature below using your mouse or finger</p>
        <div className={`adi-sig-wrap${errors.signature_data ? ' adi-sig-wrap--err' : ''}`}>
          <canvas
            ref={canvasRef}
            width={700}
            height={180}
            className="adi-sig-canvas"
            onMouseDown={startDraw}
            onMouseMove={draw}
            onMouseUp={stopDraw}
            onMouseLeave={stopDraw}
          />
          {!hasSigned && (
            <div className="adi-sig-placeholder">✍ Sign here</div>
          )}
        </div>
        <div className="adi-sig-actions">
          <button type="button" className="adi-sig-clear" onClick={clearSignature}>
            ↺ Clear &amp; Redraw
          </button>
          <span className="adi-sig-date">
            Date: {new Date().toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })}
          </span>
        </div>
        {errors.signature_data && <span className="adi-err">{errors.signature_data}</span>}
      </div>

    </div>
  );
}

